import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../lib/cart';
import { useDistributor, useAdmin, useSuperAdmin } from '../lib/auth';
import { authed } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { CreditCard, Truck, UserCog, Search as SearchIcon, Check as CheckIcon } from 'lucide-react';

/**
 * Checkout page — supports two distinct flows:
 *
 * 1) Distributor flow (default): the logged-in distributor places an order
 *    for themselves via `POST /api/orders`.
 *
 * 2) Employee/Super Admin "Order on Behalf" flow (Batch B2):
 *    when the active role is `admin` or `super_admin`, a distributor picker
 *    appears at the top. Once a distributor is selected the delivery address
 *    is pre-filled from that distributor and the order is submitted through
 *    `POST /api/admin/orders/on-behalf`. On success the admin is redirected
 *    to `/admin/orders` (Check Orders), keeping them in the admin workflow.
 */
export default function Checkout() {
    const { items, total, clear } = useCart();
    const { user, refresh } = useDistributor();
    const { admin } = useAdmin();
    const { superAdmin } = useSuperAdmin();
    const nav = useNavigate();

    // Determine active role — order of privilege matters for the header menu
    // and API auth realm.
    const activeRole = superAdmin ? 'super_admin' : (admin ? 'admin' : (user ? 'distributor' : null));
    const isOnBehalf = activeRole === 'admin' || activeRole === 'super_admin';
    const authRealm = activeRole === 'super_admin' ? 'super_admin' : (activeRole === 'admin' ? 'admin' : 'distributor');

    // Distributor picker state (on-behalf flow only)
    const [distributors, setDistributors] = useState([]);
    const [distributorsLoading, setDistributorsLoading] = useState(false);
    const [distSearch, setDistSearch] = useState('');
    const [selectedDist, setSelectedDist] = useState(null);

    const filteredDistributors = useMemo(() => {
        const t = distSearch.trim().toLowerCase();
        const list = distributors || [];
        if (!t) return list.slice(0, 50);
        return list.filter(d =>
            (d.business_name || '').toLowerCase().includes(t)
            || (d.person_name || '').toLowerCase().includes(t)
            || (d.mobile || '').includes(t)
            || (d.city || '').toLowerCase().includes(t)
        ).slice(0, 50);
    }, [distSearch, distributors]);

    // Load approved distributors when in on-behalf flow.
    useEffect(() => {
        if (!isOnBehalf) return;
        setDistributorsLoading(true);
        authed(authRealm, { method: 'GET', url: '/admin/distributors/approved-lite' })
            .then(r => setDistributors(Array.isArray(r.data) ? r.data : []))
            .catch(() => { /* ignore */ })
            .finally(() => setDistributorsLoading(false));
    }, [isOnBehalf, authRealm]);

    // Distributor-flow default address.
    const defaultAddr = useMemo(() => (user?.addresses || []).find(a => a.is_default) || (user?.addresses || [])[0] || null, [user]);
    const [addr, setAddr] = useState({
        full_name: user?.person_name || '',
        phone: user?.mobile || '',
        line1: user?.address_line || '',
        line2: '',
        city: user?.city || '',
        state: user?.state || '',
        pin_code: user?.pin_code || '',
    });
    useEffect(() => {
        if (defaultAddr) setAddr(defaultAddr);
    }, [defaultAddr]);

    // When admin picks a distributor, pre-fill delivery address from that distributor.
    useEffect(() => {
        if (!selectedDist) return;
        setAddr({
            full_name: selectedDist.person_name || selectedDist.business_name || '',
            phone: selectedDist.mobile || '',
            line1: selectedDist.address_line || '',
            line2: '',
            city: selectedDist.city || '',
            state: selectedDist.state || '',
            pin_code: selectedDist.pin_code || '',
        });
    }, [selectedDist]);

    const [payment, setPayment] = useState('cod');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const placeOrder = async () => {
        setError('');
        if (!items.length) { setError('Cart is empty'); return; }
        if (isOnBehalf && !selectedDist) { setError('Please select a distributor before placing the order.'); return; }
        for (const k of ['full_name', 'phone', 'line1', 'city', 'state', 'pin_code']) {
            if (!addr[k] || !String(addr[k]).trim()) { setError(`Please provide ${k.replace('_', ' ')}`); return; }
        }
        setBusy(true);
        try {
            if (isOnBehalf) {
                await authed(authRealm, {
                    method: 'POST',
                    url: '/admin/orders/on-behalf',
                    data: {
                        distributor_id: selectedDist.id,
                        payment_method: payment,
                        delivery_address: addr,
                        items,
                        notes,
                    },
                });
                clear();
                // Redirect admin back to Check Orders per user requirement.
                nav('/admin/orders', { replace: true });
            } else {
                const r = await authed('distributor', {
                    method: 'POST', url: '/orders', data: {
                        items,
                        address: { id: addr.id || undefined, ...addr, is_default: !!addr.is_default },
                        payment_method: payment,
                        notes,
                    }
                });
                clear();
                nav(`/orders/${r.data.id}`, { replace: true });
            }
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not place order');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <h1 className="text-2xl font-extrabold">Checkout</h1>

                {isOnBehalf && (
                    <section className="card-soft p-4 border-2 border-sdf-red/30 bg-red-50/30" data-testid="on-behalf-banner">
                        <div className="flex items-center gap-2 text-sdf-red font-bold text-xs tracking-widest">
                            <UserCog size={14} /> ORDER ON BEHALF OF A DISTRIBUTOR
                        </div>
                        <p className="text-xs text-sdf-text-2 mt-1">
                            You are signed in as <b>{activeRole === 'super_admin' ? 'Super Admin' : 'Admin'}</b>. Pick an approved distributor — the order will be created under their account and appear in their <i>Your Orders</i> as well as in <i>Check Orders</i>.
                        </p>

                        {selectedDist ? (
                            <div className="mt-3 p-3 rounded-xl bg-white border border-sdf-line flex items-center justify-between gap-3" data-testid="on-behalf-selected">
                                <div className="min-w-0">
                                    <div className="font-bold text-sdf-text truncate">{selectedDist.business_name || selectedDist.person_name}</div>
                                    <div className="text-[11px] text-sdf-text-2 truncate">
                                        {selectedDist.person_name && selectedDist.business_name && selectedDist.person_name !== selectedDist.business_name ? `${selectedDist.person_name} · ` : ''}
                                        +91 {selectedDist.mobile} {selectedDist.city ? `· ${selectedDist.city}` : ''}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedDist(null)}
                                    className="btn-outline text-xs"
                                    data-testid="on-behalf-change-distributor"
                                >Change</button>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <div className="flex items-center gap-2 bg-white border border-sdf-line rounded-xl px-3 py-2">
                                    <SearchIcon size={14} className="text-neutral-500" />
                                    <input
                                        value={distSearch}
                                        onChange={(e) => setDistSearch(e.target.value)}
                                        placeholder="Search by name, phone or city…"
                                        className="flex-1 outline-none bg-transparent py-1 text-sm"
                                        data-testid="on-behalf-search"
                                    />
                                </div>
                                <div className="mt-2 rounded-xl border border-sdf-line bg-white max-h-64 overflow-y-auto" data-testid="on-behalf-list">
                                    {distributorsLoading ? (
                                        <div className="p-3 text-xs text-sdf-text-2">Loading approved distributors…</div>
                                    ) : filteredDistributors.length === 0 ? (
                                        <div className="p-3 text-xs text-sdf-text-2">No approved distributors match your search.</div>
                                    ) : (
                                        <ul>
                                            {filteredDistributors.map(d => (
                                                <li key={d.id}>
                                                    <button
                                                        onClick={() => setSelectedDist(d)}
                                                        className="w-full text-left px-3 py-2 hover:bg-sdf-cream/40 border-b border-sdf-line/60 last:border-b-0"
                                                        data-testid={`on-behalf-pick-${d.id}`}
                                                    >
                                                        <div className="font-bold text-sm text-sdf-text line-clamp-1">{d.business_name || d.person_name || '—'}</div>
                                                        <div className="text-[11px] text-sdf-text-2 line-clamp-1">
                                                            {d.person_name && d.business_name && d.person_name !== d.business_name ? `${d.person_name} · ` : ''}
                                                            +91 {d.mobile} {d.city ? `· ${d.city}` : ''}
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                <section className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">DELIVERY ADDRESS</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <Input label="Full Name" value={addr.full_name} onChange={(v) => setAddr({ ...addr, full_name: v })} tid="co-name" />
                        <Input label="Phone" value={addr.phone} onChange={(v) => setAddr({ ...addr, phone: v })} tid="co-phone" />
                        <Input label="Address Line 1" value={addr.line1} onChange={(v) => setAddr({ ...addr, line1: v })} full tid="co-line1" />
                        <Input label="Address Line 2" value={addr.line2 || ''} onChange={(v) => setAddr({ ...addr, line2: v })} full tid="co-line2" />
                        <Input label="City" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} tid="co-city" />
                        <Input label="State" value={addr.state} onChange={(v) => setAddr({ ...addr, state: v })} tid="co-state" />
                        <Input label="PIN Code" value={addr.pin_code} onChange={(v) => setAddr({ ...addr, pin_code: v })} tid="co-pin" />
                    </div>
                </section>

                <section className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">PAYMENT METHOD</div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${payment === 'cod' ? 'border-sdf-red bg-red-50/30' : 'border-sdf-line bg-white'}`} data-testid="pay-cod">
                            <input type="radio" name="pay" value="cod" checked={payment === 'cod'} onChange={() => setPayment('cod')} className="mt-1 accent-red-700" />
                            <div>
                                <div className="font-bold text-sm flex items-center gap-2"><Truck size={16} /> Cash on Delivery</div>
                                <div className="text-xs text-sdf-text-2 mt-1">Pay in cash when your order is delivered.</div>
                            </div>
                        </label>
                        <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${payment === 'credit' ? 'border-sdf-red bg-red-50/30' : 'border-sdf-line bg-white'}`} data-testid="pay-credit">
                            <input type="radio" name="pay" value="credit" checked={payment === 'credit'} onChange={() => setPayment('credit')} className="mt-1 accent-red-700" />
                            <div>
                                <div className="font-bold text-sm flex items-center gap-2"><CreditCard size={16} /> Credit Purchase</div>
                                <div className="text-xs text-sdf-text-2 mt-1">Order placed on credit; settlement per business agreement.</div>
                            </div>
                        </label>
                    </div>
                </section>

                <section className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">YOUR ORDER</div>
                    <div className="mt-3 divide-y divide-sdf-line">
                        {items.map((it) => (
                            <div key={`${it.product_id}-${it.variant_id}-${it.flavour_id}`} className="py-2 text-sm flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold line-clamp-1">{it.flavour_name}</div>
                                    <div className="text-xs text-sdf-text-2">{it.size_label} × {it.quantity}</div>
                                </div>
                                <div className="font-bold">{formatMoney(it.price * it.quantity)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">ORDER SUMMARY</div>
                    <div className="flex justify-between mt-3 pt-3 border-t border-sdf-line"><span className="font-bold">Total Amount</span><span className="font-extrabold text-lg" data-testid="co-total">{formatMoney(total)}</span></div>
                </section>

                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes (optional)" className="w-full border border-sdf-line rounded-xl px-4 py-3 text-sm bg-white outline-none" rows={2} />

                {error && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="checkout-error">{error}</div>}

                <button
                    onClick={placeOrder}
                    disabled={busy || items.length === 0 || (isOnBehalf && !selectedDist)}
                    className="btn-red-lg"
                    data-testid="place-order"
                >
                    {busy
                        ? 'Placing order…'
                        : isOnBehalf
                            ? (payment === 'cod' ? 'PLACE COD ORDER ON BEHALF' : 'PLACE CREDIT ORDER ON BEHALF')
                            : (payment === 'cod' ? 'PLACE COD ORDER' : 'PLACE CREDIT ORDER')}
                </button>
            </main>
            <Footer />
        </div>
    );
}

function Input({ label, value, onChange, full, tid }) {
    return (
        <div className={full ? 'sm:col-span-2' : ''}>
            <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">{label.toUpperCase()}</label>
            <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-2.5 outline-none text-sm bg-white" data-testid={tid} />
        </div>
    );
}
